import { useCallback, useEffect, useState } from 'react';
import {
  TUTORIAL_STEPS,
  TUTORIAL_TOTAL_STEPS,
  type TutorialEvent,
  type TutorialStep,
} from './tutorialConfig';

/** Clés localStorage partagées avec le Dashboard (bouton d'activation). */
export const LS_TUTORIAL_ENABLED = 'tutorialEnabled';
export const LS_TUTORIAL_STEP = 'tutorialStep';
export const LS_TUTORIAL_COMPLETED = 'tutorialCompleted';

function readInitialStep(): number {
  const raw = Number(localStorage.getItem(LS_TUTORIAL_STEP));
  if (Number.isFinite(raw) && raw >= 0 && raw < TUTORIAL_TOTAL_STEPS) {
    return raw;
  }
  return 0;
}

export interface UseTutorialResult {
  /** Le tutoriel est-il en cours ? */
  active: boolean;
  /** Index de l'étape courante. */
  step: number;
  /** Métadonnées de l'étape courante (null si inactif / hors bornes). */
  stepData: TutorialStep | null;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  /** Avance d'une étape (ou termine si dernière). */
  next: () => void;
  /** Recule d'une étape. */
  back: () => void;
  /** Abandonne le tutoriel (le marque terminé). */
  skip: () => void;
  /** Termine le tutoriel proprement. */
  finish: () => void;
  /**
   * Remonte un événement de jeu. Fait avancer l'étape courante uniquement si
   * sa condition (`gate`) correspond à l'événement.
   */
  reportEvent: (evt: TutorialEvent) => void;
}

/**
 * Moteur du tutoriel interactif.
 *
 * Persistance 100 % localStorage :
 * - `tutorialEnabled`   : le bouton du Dashboard (« Activer / Refaire le tutoriel »).
 * - `tutorialStep`      : reprise à l'étape courante si le joueur recharge.
 * - `tutorialCompleted` : mémorise qu'il a déjà été fait (pour proposer « Refaire »).
 *
 * Le moteur ne dépend d'aucun serveur : il observe des événements de jeu
 * (`reportEvent`) déclenchés par la carte (bâtiment placé, personnel recruté, etc.).
 */
export default function useTutorial(): UseTutorialResult {
  const [active, setActive] = useState<boolean>(
    () => localStorage.getItem(LS_TUTORIAL_ENABLED) === 'true',
  );
  const [step, setStep] = useState<number>(readInitialStep);

  // Persiste l'étape courante tant que le tutoriel est actif.
  useEffect(() => {
    if (active) {
      localStorage.setItem(LS_TUTORIAL_STEP, String(step));
    }
  }, [active, step]);

  const finish = useCallback(() => {
    setActive(false);
    setStep(0);
    localStorage.setItem(LS_TUTORIAL_ENABLED, 'false');
    localStorage.setItem(LS_TUTORIAL_COMPLETED, '1');
    localStorage.setItem(LS_TUTORIAL_STEP, '0');
  }, []);

  const goTo = useCallback(
    (target: number) => {
      if (target >= TUTORIAL_TOTAL_STEPS) {
        finish();
        return;
      }
      setStep(Math.max(0, target));
    },
    [finish],
  );

  const next = useCallback(() => goTo(step + 1), [goTo, step]);
  const back = useCallback(() => goTo(step - 1), [goTo, step]);
  const skip = useCallback(() => finish(), [finish]);

  const stepData = active ? TUTORIAL_STEPS[step] ?? null : null;

  const reportEvent = useCallback(
    (evt: TutorialEvent) => {
      if (stepData && stepData.gate === evt) {
        next();
      }
    },
    [stepData, next],
  );

  return {
    active,
    step,
    stepData,
    totalSteps: TUTORIAL_TOTAL_STEPS,
    isFirstStep: step === 0,
    isLastStep: step === TUTORIAL_TOTAL_STEPS - 1,
    next,
    back,
    skip,
    finish,
    reportEvent,
  };
}
